import { useEffect, useState } from 'react';
import { deleteField, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../shared/lib/firebase';
import { useAuth } from '../auth/useAuth';
import {
  type GeneralSettings,
  DEFAULT_SECURITY_SETTINGS,
} from '../../shared/types/settings';

const DEFAULT_BUSINESS_HOURS: GeneralSettings['businessHours'] = {
  '0': { start: '00:00', end: '00:00', closed: true },
  '1': { start: '08:00', end: '18:00' },
  '2': { start: '08:00', end: '18:00' },
  '3': { start: '08:00', end: '18:00' },
  '4': { start: '08:00', end: '18:00' },
  '5': { start: '08:00', end: '18:00' },
  '6': { start: '00:00', end: '00:00', closed: true },
};

export const DEFAULT_GENERAL_SETTINGS: Omit<GeneralSettings, 'updatedAt' | 'updatedBy'> = {
  companyName: '',
  timezone: 'America/Sao_Paulo',
  businessHours: DEFAULT_BUSINESS_HOURS,
  security: DEFAULT_SECURITY_SETTINGS,
};

const ehMapa = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;

/** Dentro de mapa, campo undefined some (o mapa é regravado inteiro). */
function semIndefinidos(v: unknown): unknown {
  if (!ehMapa(v)) return v;
  return Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined).map(([k, x]) => [k, semIndefinidos(x)]));
}

/**
 * O SDK do cliente recusa undefined (sem ignoreUndefinedProperties). No topo, campo esvaziado vira deleteField()
 * — omitir manteria o valor antigo no doc. Toda tela de Ajustes passa por aqui.
 */
function paraUpdate(patch: Partial<GeneralSettings>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, v === undefined ? deleteField() : semIndefinidos(v)]));
}

export function useSettings() {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<GeneralSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'system_settings/general'),
      (snap) => {
        setSettings(snap.exists() ? (snap.data() as GeneralSettings) : null);
        setLoading(false);
      },
      (err) => {
        console.error('[useSettings] snapshot error', err);
        setError(err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  async function update(patch: Partial<GeneralSettings>): Promise<void> {
    if (!currentUser) throw new Error('Não autenticado.');
    const ref = doc(db, 'system_settings/general');
    if (!settings) {
      await setDoc(ref, {
        ...DEFAULT_GENERAL_SETTINGS,
        ...(semIndefinidos(patch) as Partial<GeneralSettings>),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
    } else {
      await updateDoc(ref, {
        ...paraUpdate(patch),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
    }
  }

  return { settings, loading, error, update };
}
